import { reflectKeys } from "@utils/key.reflactor";
import { NotificationSubscriptionSettingMap } from "@modules/notification/types.ts";

export const NAMESPACE = {
  AUTH: "auth",
};
export const NOTIFICATION_POLLING_INTERVAL = 60000; // 1 min

export const DOWNLOAD_EVENT_TYPE = reflectKeys([
  "MARK_ZIP_FILES_DONE",
  "RESPONSE_ZIP_FILES_DONE",
  "INDIVIDUAL_ASSIGNMENT_REPORT_DOWNLOAD",
  "RESPONSE_REPORT_DOWNLOAD",
  "USAGE_REPORT_DOWNLOAD",
  "LEARNING_PROGRESS_ASSIGNMENT_ZIP_FILES_DONE",
  "LEARNING_PROGRESS_SELF_STUDY_ZIP_FILES_DONE",
  "LEARNING_PROGRESS_SELF_STUDY_ALS_ZIP_FILES_DONE",
  "ZIP_FILES_DONE", //deprecated eventType
]);

export const NOTIFICATION_AUDIENCE = reflectKeys([
  "ALL",
  "ALL_STUDENTS",
  "ALL_TEACHERS",
  "FORM_CLASS",
  "INDIVIDUAL",
]);

export const NOTIFICATION_SCOPE = reflectKeys(["SYSTEM", "SCHOOL", "GROUP"]);

export const NOTIFICATION_CONTEXT = reflectKeys(["PAGE", "PANEL", "DASHBOARD"]);

export const SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS = reflectKeys([
  "ALL",
  "ALL_TEACHERS",
  "ALL_STUDENTS",
  "FORM_CLASS",
]);

export const SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS_LABEL = {
  ALL: "notification_audience_label_all",
  ALL_TEACHERS: "notification_audience_label_all_teachers",
  ALL_STUDENTS: "notification_audience_label_all_students",
  FORM_CLASS: "notification_audience_label_form_classes",
};

export const SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS = reflectKeys([
  "ALL",
  "ALL_TEACHERS",
  "ALL_STUDENTS",
]);

export const SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS_LABEL = {
  ALL: "notification_audience_label_all",
  ALL_TEACHERS: "notification_audience_label_all_teachers",
  ALL_STUDENTS: "notification_audience_label_all_students",
};

export const NOTIFICATION_FIRST_LEVEL_CATEGORY = {
  GENERAL: { value: "GENERAL", label: "email_notification_first_category_general_label" },
  CLASS_GROUP: { value: "CLASS_GROUP", label: "email_notification_first_category_group_label" },
  COMMUNITY: { value: "COMMUNITY", label: "email_notification_first_category_community_label" },
  CACO: { value: "CACO", label: "email_notification_first_category_ca_co_label" },
  SCHOOL_ADMIN: {
    value: "SCHOOL_ADMIN",
    label: "email_notification_first_category_school_admin_label",
  },
  VLE_ADMIN: { value: "VLE_ADMIN", label: "email_notification_first_category_vle_admin_label" },
  ALL: { value: "ALL", label: "email_notification_first_category_all_label" },
} as const;

export const NOTIFICATION_SECOND_LEVEL_CATEGORY = {
  //  General
  SYSTEM_ANNOUNCEMENT: {
    value: "SYSTEM_ANNOUNCEMENT",
    label: "email_notification_second_category_system_announcement_label",
  },
  RESOURCE_STATUS: {
    value: "RESOURCE_STATUS",
    label: "email_notification_second_category_resource_status_label",
  },
  TRANSCODING: {
    value: "TRANSCODING",
    label: "email_notification_second_category_transcoding_label",
  },
  OTHERS: { value: "OTHERS", label: "email_notification_second_category_others_label" },
  COMMUNITY_ANNOUNCEMENT: {
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "email_notification_second_category_community_announcement_label",
  },
  //  Group
  CLASS_GROUP_ANNOUNCEMENT: {
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "email_notification_second_category_group_announcement_label",
  },
  FORUM: { value: "FORUM", label: "email_notification_second_category_forum_label" },
  ASSIGNMENT: { value: "ASSIGNMENT", label: "email_notification_second_category_assignment_label" },
  RESOURCE: {
    value: "RESOURCE",
    label: "email_notification_second_category_group_announcement_label",
  },
  MEMBERSHIP: { value: "MEMBERSHIP", label: "email_notification_second_category_membership_label" },
  //  CA/CO
  CONTENT_MAP: {
    value: "CONTENT_MAP",
    label: "email_notification_second_category_content_map_label",
  },
  LESSON_AND_COURSES: {
    value: "LESSON_AND_COURSES",
    label: "email_notification_second_category_lesson_label",
  },
  MEDIA: { value: "MEDIA", label: "email_notification_second_category_media_label" },
  //  School Admin
  REPORTS: { value: "REPORTS", label: "email_notification_second_category_reports_label" },
  ACCOUNT: { value: "ACCOUNT", label: "email_notification_second_category_account_label" },
} as const;

export const NOTIFICATION_THIRD_LEVEL_CATEGORY = {
  ASSIGNMENT_DUE_DATE: {
    value: "ASSIGNMENT_DUE_DATE",
    label: "email_notification_class_group_assignment_due_date_label",
  },
  ASSIGNMENT_STATUS_UPDATE: {
    value: "ASSIGNMENT_STATUS_UPDATE",
    label: "email_notification_class_group_assignment_status_update_label",
  },
  TEACHER_COMMENT: {
    value: "TEACHER_COMMENT",
    label: "email_notification_class_group_assignment_teacher_to_teacher_comment_label",
  },
  STUDENT_COMMENT: {
    value: "STUDENT_COMMENT",
    label: "email_notification_class_group_assignment_student_to_teacher_label",
  },
  DOWNLOAD_ALERT: {
    value: "DOWNLOAD_ALERT",
    label: "email_notification_class_group_assignment_download_alert_label",
  },
  SHARING_PERMISSION: {
    value: "SHARING_PERMISSION",
    label: "email_notification_class_group_assignment_sharing_permission_label",
  },
  GAMIFICATION: {
    value: "GAMIFICATION",
    label: "email_notification_class_group_assignment_gamification_label",
  },
} as const;

export const NOTIFICATION_ADMIN_CATEGORIES = reflectKeys([
  "CACO-CONTENT_MAP",
  "CACO-LESSON_AND_COURSES",
  "CACO-MEDIA",
  "CACO-REPORTS",
  "SCHOOL_ADMIN-REPORTS",
]);

export const NOTIFICATION_ADMIN_CATEGORIES_FOR_TEACHER_AND_STUDENT = {
  "GENERAL-ACCOUNT": "GENERAL-ACCOUNT",
};

export const NOTIFICATION_EVENT_TYPE = reflectKeys([
  "ADD_TO_STUDENT_GROUP",
  "REMOVE_FROM_STUDENT_GROUP",
  "APP_NOTIFICATION",
  "MARK_ASSIGNMENT_SECTION_COMPLETE",
]);

export const NOTIFICATION_USER_SUBSCRIPTION_LABEL: NotificationSubscriptionSettingMap = {
  ASSIGNMENT: {
    value: "CLASS_GROUP-ASSIGNMENT",
    label: "notification_subscription_label_assignments",
  },
  GROUP_ANNOUNCEMENT: {
    value: "CLASS_GROUP-CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_subscription_label_announcements",
  },
  FORUM: {
    value: "CLASS_GROUP-FORUM",
    label: "notification_subscription_label_forums",
  },
  RESOURCE: {
    value: "CLASS_GROUP-RESOURCE",
    label: "notification_subscription_label_resources",
  },
  RESOURCE_STATUS: {
    value: "GENERAL-RESOURCE_STATUS",
    label: "notification_subscription_label_resource_status",
  },
  ADMIN: {
    value: "ADMIN",
    label: "notification_subscription_label_admin",
  },
};

export const SUBSCRIPTION_TYPE = {
  ALL_GROUP: "ALL_GROUP",
  PINNED_GROUP: "PINNED_GROUP",
  COMMUNITY: "COMMUNITY",
} as const;

/**
 * For notifications with only Community toggles
 */
export const COMMUNITY_SETTING_AVAILABLE_FOR_NOTIFICATION = [
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.GROUP_ANNOUNCEMENT.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.FORUM.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.RESOURCE.value,
];

/**
 * For notifications with only Group toggles
 */
export const GROUP_SETTING_AVAILABLE_FOR_NOTIFICATION = [
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.ASSIGNMENT.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.GROUP_ANNOUNCEMENT.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.FORUM.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.RESOURCE.value,
];

/**
 * For notifications without Group / Community toggles
 */
export const NOTIFICATION_INDIVIDUAL_TOGGLE = [
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.RESOURCE_STATUS.value,
  NOTIFICATION_USER_SUBSCRIPTION_LABEL.ADMIN.value,
];

/**
 * Available notification for CLASS_GROUP-ASSIGNMENT type notifications by role
 */
export const NOTIFICATION_THIRD_LV_CATEGORY_BY_ROLE = {
  TEACHER: [
    NOTIFICATION_THIRD_LEVEL_CATEGORY.TEACHER_COMMENT.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.STUDENT_COMMENT.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.DOWNLOAD_ALERT.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.SHARING_PERMISSION.value,
  ],
  STUDENT: [
    NOTIFICATION_THIRD_LEVEL_CATEGORY.ASSIGNMENT_DUE_DATE.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.ASSIGNMENT_STATUS_UPDATE.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.TEACHER_COMMENT.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.STUDENT_COMMENT.value,
    NOTIFICATION_THIRD_LEVEL_CATEGORY.GAMIFICATION.value,
  ],
} as const;

export const ALL_OPTION = {
  ALL: { name: "ALL", value: "ALL", label: "notification_audience_label_all" },
};

export const VIEW_OPTIONS_LIST = {
  VLE_ADMIN: { name: "VLE ADMIN", value: "VLE_ADMIN", label: "notification_view_label_vle_admin" },
  ADMIN_ROLES: {
    name: "ADMIN ROLES",
    value: "ADMIN ROLES",
    label: "notification_view_label_admin_roles",
    header: true,
  },
  CACO: { name: "CACO", value: "CACO", label: "notification_view_label_content_approver_officer" },
  SCHOOL_ADMIN: {
    name: "SCHOOL_ADMIN",
    value: "SCHOOL_ADMIN",
    label: "notification_view_label_school_admin",
  },
  GENERAL: { name: "GENERAL", value: "GENERAL", label: "notification_view_label_general" },
  ALL: { name: "ALL", value: "ALL", label: "notification_audience_label_all" },
};

export const STATUS_OPTIONS_LIST = {
  READ: { name: "READ", value: "READ", label: "notification_status_label_read" },
  UNREAD: { name: "UNREAD", value: "UNREAD", label: "notification_status_label_unread" },
  FLAGGED: { name: "FLAGGED", value: "FLAGGED", label: "notification_status_label_flagged" },
  UNFLAGGED: {
    name: "UNFLAGGED",
    value: "UNFLAGGED",
    label: "notification_status_label_not_flagged",
  },
};

export const STATUS_TABS_LIST = {
  ALL: { name: "ALL", value: "ALL", label: "notification_audience_label_all" },
  UNREAD: { name: "UNREAD", value: "UNREAD", label: "notification_status_label_unread" },
  FLAGGED: { name: "FLAGGED", value: "FLAGGED", label: "notification_status_label_flagged" },
};

export const TYPE_OPTIONS_ALL = {
  SYSTEM_ANNOUNCEMENT: {
    name: "SYSTEM_ANNOUNCEMENT",
    value: "SYSTEM_ANNOUNCEMENT",
    label: "notification_type_label_school_system_announcements",
  },
  RESOURCE_STATUS: {
    name: "RESOURCE_STATUS",
    value: "RESOURCE_STATUS",
    label: "notification_type_label_resource_status",
  },
  TRANSCODING: {
    name: "TRANSCODING",
    value: "TRANSCODING",
    label: "notification_type_label_transcoding_transcription",
  },
  OTHERS: { name: "OTHERS", value: "OTHERS", label: "notification_type_label_others" },
  CLASS_GROUP_ANNOUNCEMENT: {
    name: "CLASS_GROUP_ANNOUNCEMENT",
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_type_label_group_announcements",
  },
  COMMUNITY_ANNOUNCEMENT: {
    name: "COMMUNITY_ANNOUNCEMENT",
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "notification_type_label_community_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignments",
  },
  CONTENT_MAP: {
    name: "CONTENT_MAP",
    value: "CONTENT_MAP",
    label: "notification_type_label_content_map",
  },
  LESSON_AND_COURSES: {
    name: "LESSON_AND_COURSES",
    value: "LESSON_AND_COURSES",
    label: "notification_type_label_lesson",
  },
  MEDIA: { name: "MEDIA", value: "MEDIA", label: "notification_type_label_media" },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
  ACCOUNT: {
    name: "ACCOUNT",
    value: "ACCOUNT",
    label: "notification_type_label_account",
  },
};

export const TYPE_OPTIONS_GENERAL = {
  ACCOUNT: {
    name: "ACCOUNT",
    value: "ACCOUNT",
    label: "notification_type_label_account",
  },
  OTHERS: { name: "OTHERS", value: "OTHERS", label: "notification_type_label_others" },
  RESOURCE_STATUS: {
    name: "RESOURCE_STATUS",
    value: "RESOURCE_STATUS",
    label: "notification_type_label_resource_status",
  },
  SYSTEM_ANNOUNCEMENT: {
    name: "SYSTEM_ANNOUNCEMENT",
    value: "SYSTEM_ANNOUNCEMENT",
    label: "notification_type_label_school_system_announcements",
  },
  TRANSCODING: {
    name: "TRANSCODING",
    value: "TRANSCODING",
    label: "notification_type_label_transcoding_transcription",
  },
};

export const TYPE_OPTIONS_OTHERS = {
  RESOURCE_STATUS: {
    name: "RESOURCE_STATUS",
    value: "RESOURCE_STATUS",
    label: "notification_type_label_resource_status",
  },
  TRANSCODING: {
    name: "TRANSCODING",
    value: "TRANSCODING",
    label: "notification_type_label_transcoding_transcription",
  },
  OTHERS: { name: "OTHERS", value: "OTHERS", label: "notification_type_label_others" },
};

export const TYPE_OPTIONS_CACO = {
  CONTENT_MAP: {
    name: "CONTENT_MAP",
    value: "CONTENT_MAP",
    label: "notification_type_label_content_map",
  },
  LESSON_AND_COURSES: {
    name: "LESSON_AND_COURSES",
    value: "LESSON_AND_COURSES",
    label: "notification_type_label_lesson",
  },
  MEDIA: { name: "MEDIA", value: "MEDIA", label: "notification_type_label_media" },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_COMMUNITY = {
  COMMUNITY_ANNOUNCEMENT: {
    name: "COMMUNITY_ANNOUNCEMENT",
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "notification_type_label_community_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_COMMUNITY_TEACHER = {
  COMMUNITY_ANNOUNCEMENT: {
    name: "COMMUNITY_ANNOUNCEMENT",
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "notification_type_label_community_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_COMMUNITY_STUDENT = {
  COMMUNITY_ANNOUNCEMENT: {
    name: "COMMUNITY_ANNOUNCEMENT",
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "notification_type_label_community_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
};

export const TYPE_OPTIONS_CLASS_GROUP = {
  CLASS_GROUP_ANNOUNCEMENT: {
    name: "CLASS_GROUP_ANNOUNCEMENT",
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_type_label_group_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_CLASS_GROUP_TEACHER = {
  CLASS_GROUP_ANNOUNCEMENT: {
    name: "CLASS_GROUP_ANNOUNCEMENT",
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_type_label_group_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_CLASS_GROUP_STUDENT = {
  CLASS_GROUP_ANNOUNCEMENT: {
    name: "CLASS_GROUP_ANNOUNCEMENT",
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_type_label_group_announcements",
  },
  FORUM: { name: "FORUM", value: "FORUM", label: "notification_type_label_forum" },
  ASSIGNMENT: {
    name: "ASSIGNMENT",
    value: "ASSIGNMENT",
    label: "notification_type_label_assignment",
  },
  MEMBERSHIP: {
    name: "MEMBERSHIP",
    value: "MEMBERSHIP",
    label: "notification_type_label_membership",
  },
  RESOURCE: { name: "RESOURCE", value: "RESOURCE", label: "notification_type_label_resource" },
};

export const TYPE_OPTIONS_SCHOOL_ADMIN = {
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const TYPE_OPTIONS_ANNOUNCEMENT = {
  CLASS_GROUP_ANNOUNCEMENT: {
    name: "CLASS_GROUP_ANNOUNCEMENT",
    value: "CLASS_GROUP_ANNOUNCEMENT",
    label: "notification_type_label_group_announcements",
  },
  COMMUNITY_ANNOUNCEMENT: {
    name: "COMMUNITY_ANNOUNCEMENT",
    value: "COMMUNITY_ANNOUNCEMENT",
    label: "notification_type_label_community_announcements",
  },
  SYSTEM_ANNOUNCEMENT: {
    name: "SYSTEM_ANNOUNCEMENT",
    value: "SYSTEM_ANNOUNCEMENT",
    label: "notification_type_label_school_system_announcements",
  },
};

export const TYPE_OPTIONS_REPORTS = {
  REPORTS: { name: "REPORTS", value: "REPORTS", label: "notification_type_label_reports" },
};

export const NOTIFICATION_SECOND_LEVEL_CATEGORY_EVENT_ICON_MAPS = {
  SYSTEM_ANNOUNCEMENT: {
    APP_NOTIFICATION: "Speaker32",
    NEW_ANNOUNCEMENT: "Speaker32",
  },
  CLASS_GROUP_ANNOUNCEMENT: {
    ADD_TO_STUDENT_GROUP: "Class32",
    REMOVE_FROM_STUDENT_GROUP: "Class32",
    NEW_ANNOUNCEMENT: "Speaker32",
  },
  COMMUNITY_ANNOUNCEMENT: {
    ADD_TO_STUDENT_GROUP: "Class32",
    REMOVE_FROM_STUDENT_GROUP: "Class32",
    NEW_ANNOUNCEMENT: "Speaker32",
  },
  RESOURCE: {
    NEW_ASSIGNMENT: "Lesson32",
    UPDATE_ASSIGNMENT: "Lesson32",
    NODES_RECOMMENDATION_FOR_ALS: "Lesson32",
    ANNOTATION_MENTION: "Lesson32",
    ANNOTATION_COMMENT_MENTION: "Lesson32",
  },
  ASSIGNMENT: {
    ZIP_FILES_DOWNLOAD: "Download32",
    QUESTION_ATTEMPT_FEEDBACK: "Feedback32",
    QUIZ_ATTEMPT_FEEDBACK: "Feedback32",
    NEW_TOPIC_RESOURCE_COMMENT: "Feedback32",
    DUESOON_ASSIGNMENT: "Lesson32",
    NEW_ASSIGNMENT: "Lesson32",
    GAME_AWARD: "Lesson32",
    UPDATE_TEACHER_ANNOTATION: "Lesson32",
    RELEASE_MARKS: "Lesson32",
    UNSUBMIT_RESOURCE: "Lesson32",
    UPDATE_ASSIGNMENT: "Lesson32",
    MARK_ASSIGNMENT_SECTION_COMPLETE: "Lesson32",
    MARK_ASSIGNMENT_SECTION_INCOMPLETE: "Lesson32",
    MARK_ASSIGNMENT_COMPLETE: "Lesson32",
    MARK_ASSIGNMENT_INCOMPLETE: "Lesson32",
    MARK_ZIP_FILES_DONE: "Download32",
    RESPONSE_ZIP_FILES_DONE: "Download32",
    ATTEMPT_IMPORT_COMPLETE: "Success32",
    UNSUBMIT_MARKS: "Lesson32",
    TEACHER_SUBMIT_MARKS: "Lesson32",
    HEATMAP_NOTIFICATION: "Lesson32",
    DISCUSSION_POST_MENTION: "Lesson32",
    DISCUSSION_POST_COMMENT_MENTION: "Lesson32",
    QUESTION_ATTEMPT_FEEDBACK_MENTION: "Lesson32",
    ANNOTATION_MENTION: "Lesson32",
    ANNOTATION_COMMENT_MENTION: "Lesson32",
    QUIZ_ATTEMPT_FEEDBACK_MENTION: "Lesson32",
    POST_MENTION: "Lesson32",
    POST_COMMENT_MENTION: "Lesson32",
    ASSIGNMENT_SHARING_ENABLED: "SharingEnabled32",
    ASSIGNMENT_REMIND_ENABLE_SHARING: "SharingEnabled32",
    COMMENT_CORRECTED: "Comment32",
  },
  RESOURCE_STATUS: {
    RESOURCE_REJECTED: "Error32",
    RESOURCE_APPROVED: "Success32",
    RESOURCE_SUBMIT_FOR_APPROVAL: "Success32",
    APP_NOTIFICATION: "Lesson32",
    TEACHER_REVIEW_NOTIFICATION: "Review32",
  },
  FORUM: {
    UPDATE_POST: "Chat32",
    UPDATE_POLL: "Chat32",
    UPDATE_TOPIC: "Chat32",
    NEW_POST: "Chat32",
    NEW_POLL: "Chat32",
    NEW_TOPIC: "Chat32",
    NEW_TOPIC_RESOURCE_COMMENT: "Feedback32",
    POST_MENTION: "Chat32",
    POST_COMMENT_MENTION: "Chat32",
  },
  LESSON_AND_COURSES: {
    RESOURCE_REJECTED: "Error32",
    RESOURCE_APPROVED: "Success32",
    RESOURCE_SUBMIT_FOR_APPROVAL: "Success32",
    RESOURCE_WORKING_COPY_MODIFIED: "Success32",
    ANNOTATION_MENTION: "Lesson32",
    ANNOTATION_COMMENT_MENTION: "Lesson32",
  },
  TRANSCODING: {
    VIDEO_TRANSCODED: "Media32",
    MEDIA_TRANSCRIBED: "Media32",
  },
  MEDIA: {
    MEDIA_UNPUBLISHED: "Media32",
    MEDIA_SUBMIT_FOR_UNPUBLISH: "Media32",
    MEDIA_SUBMIT_FOR_APPROVAL: "Media32",
    MEDIA_EXPIRING: "Media32",
    MEDIA_APPROVED: "Media32",
    MEDIA_REJECTED: "Media32",
  },
  CONTENT_MAP: {
    CONTENT_MAP_UPLOADED: "ContentMap32",
    CONTENT_MAP_DUPLICATED: "ContentMap32",
    CONTENT_MAP_SAVE: "ContentMap32",
    CONTENT_MAP_TAGGING: "ContentMap32",
  },
  MEMBERSHIP: {
    GROUP_REQUEST_ACCESS: "Class32",
    REMOVE_FROM_STUDENT_GROUP: "Class32",
    REMOVE_FROM_COMMUNITY: "Community32",
    COMMUNITY_REQUEST_ACCESS: "Community32",
    ADD_TO_COMMUNITY: "Community32",
    ADD_TO_STUDENT_GROUP: "Class32",
  },
  REPORTS: {
    INDIVIDUAL_ASSIGNMENT_REPORT_DOWNLOAD: "Download32",
    RESPONSE_REPORT_DOWNLOAD: "Download32",
    USAGE_REPORT_DOWNLOAD: "Download32",
    LEARNING_PROGRESS_ASSIGNMENT_ZIP_FILES_DONE: "Download32",
    LEARNING_PROGRESS_SELF_STUDY_ZIP_FILES_DONE: "Download32",
    LEARNING_PROGRESS_SELF_STUDY_ALS_ZIP_FILES_DONE: "Download32",
    ALS_REPORT_DOWNLOAD: "Download32",
  },
  OTHERS: {
    AWARD_DIGITALBADGE: "DigitalBadge32",
    ACCOUNT_AUDIT_EMPTY_VERIFIER: "Warning32",
    SET_EMAIL_ADDRESS: "Gear32",
    ATTEMPT_IMPORT_FAILED: "Error32",
    BUDDY_REQUESTED: "BuddyList32",
    BUDDY_ACCEPTED: "BuddyList32",
  },
  ACCOUNT: {
    // subCategory
    ACCOUNT_DEACTIVATION: "User32", // might need to check this again
  },
};

export const ADD_TEACHER_FILTER_TAG_LABELS = [
  {
    role: "search_filter_add_teacher_roles_label",
    school: "search_filter_add_student_group_schools_label",
  },
];

export const EVENT_TYPE = {
  RESOURCE_APPROVED: "RESOURCE_APPROVED",
  RESOURCE_REJECTED: "RESOURCE_REJECTED",
  RESOURCE_SUBMIT_FOR_APPROVAL: "RESOURCE_SUBMIT_FOR_APPROVAL",
  RESOURCE_WORKING_COPY_MODIFIED: "RESOURCE_WORKING_COPY_MODIFIED",
  RESOURCE_NEWLY_TAGGED: "RESOURCE_NEWLY_TAGGED",
  MEDIA_UNPUBLISHED: "MEDIA_UNPUBLISHED",
  MEDIA_EXPIRING: "MEDIA_EXPIRING",
  MEDIA_SUBMIT_FOR_UNPUBLISH: "MEDIA_SUBMIT_FOR_UNPUBLISH",
  MEDIA_SUBMIT_FOR_APPROVAL: "MEDIA_SUBMIT_FOR_APPROVAL",
  MEDIA_APPROVED: "MEDIA_APPROVED",
  MEDIA_REJECTED: "MEDIA_REJECTED",
  VIDEO_TRANSCODED: "VIDEO_TRANSCODED",
  MEDIA_TRANSCRIBED: "MEDIA_TRANSCRIBED",
  CONTENT_MAP_UPDATE: "CONTENT_MAP_UPDATE",
  CONTENT_MAP_UPLOADED: "CONTENT_MAP_UPLOADED",
  CONTENT_MAP_DUPLICATED: "CONTENT_MAP_DUPLICATED",
  CONTENT_MAP_SAVE: "CONTENT_MAP_SAVE",
  CONTENT_MAP_TAGGING: "CONTENT_MAP_TAGGING",
  NEW_ASSIGNMENT: "NEW_ASSIGNMENT",
  UPDATE_ASSIGNMENT: "UPDATE_ASSIGNMENT",
  DUESOON_ASSIGNMENT: "DUESOON_ASSIGNMENT",
  MARK_ASSIGNMENT_INCOMPLETE: "MARK_ASSIGNMENT_INCOMPLETE",
  MARK_ASSIGNMENT_COMPLETE: "MARK_ASSIGNMENT_COMPLETE",
  MARK_ASSIGNMENT_SECTION_COMPLETE: "MARK_ASSIGNMENT_SECTION_COMPLETE",
  MARK_ASSIGNMENT_SECTION_INCOMPLETE: "MARK_ASSIGNMENT_SECTION_INCOMPLETE",
  MARK_ASSIGNMENT_ACTIVITY_COMPLETE: "MARK_ASSIGNMENT_ACTIVITY_COMPLETE",
  MARK_ASSIGNMENT_ACTIVITY_INCOMPLETE: "MARK_ASSIGNMENT_ACTIVITY_INCOMPLETE",
  ZIP_FILES_DONE: "ZIP_FILES_DONE",
  MARK_ZIP_FILES_DONE: "MARK_ZIP_FILES_DONE",
  RESPONSE_ZIP_FILES_DONE: "RESPONSE_ZIP_FILES_DONE",
  LEARNING_PROGRESS_ASSIGNMENT_ZIP_FILES_DONE: "LEARNING_PROGRESS_ASSIGNMENT_ZIP_FILES_DONE",
  LEARNING_PROGRESS_SELF_STUDY_ZIP_FILES_DONE: "LEARNING_PROGRESS_SELF_STUDY_ZIP_FILES_DONE",
  LEARNING_PROGRESS_SELF_STUDY_ALS_ZIP_FILES_DONE:
    "LEARNING_PROGRESS_SELF_STUDY_ALS_ZIP_FILES_DONE",
  NEW_COMMENT: "NEW_COMMENT",
  APP_NOTIFICATION: "APP_NOTIFICATION",
  NEW_ANNOTATION_SHAREE: "NEW_ANNOTATION_SHAREE",
  DELETED_ANNOTATION_SHAREE: "DELETED_ANNOTATION_SHAREE",
  SET_EMAIL_ADDRESS: "SET_EMAIL_ADDRESS",
  NEW_ANNOUNCEMENT: "NEW_ANNOUNCEMENT",
  RELEASE_MARKS: "RELEASE_MARKS",
  UNSUBMIT_MARKS: "UNSUBMIT_MARKS",
  UNSUBMIT_RESOURCE: "UNSUBMIT_RESOURCE",
  QUESTION_ATTEMPT_FEEDBACK: "QUESTION_ATTEMPT_FEEDBACK",
  QUIZ_ATTEMPT_FEEDBACK: "QUIZ_ATTEMPT_FEEDBACK",
  INDIVIDUAL_ASSIGNMENT_REPORT_DOWNLOAD: "INDIVIDUAL_ASSIGNMENT_REPORT_DOWNLOAD",
  RESPONSE_REPORT_DOWNLOAD: "RESPONSE_REPORT_DOWNLOAD",
  LRS_REPORT_DOWNLOAD: "LRS_REPORT_DOWNLOAD",
  ALS_REPORT_DOWNLOAD: "ALS_REPORT_DOWNLOAD",
  GAME_AWARD: "GAME_AWARD",
  NEW_POST: "NEW_POST",
  UPDATE_POST: "UPDATE_POST",
  NEW_POLL: "NEW_POLL",
  UPDATE_POLL: "UPDATE_POLL",
  NEW_TOPIC: "NEW_TOPIC",
  UPDATE_TOPIC: "UPDATE_TOPIC",
  UPDATE_TEACHER_ANNOTATION: "UPDATE_TEACHER_ANNOTATION",
  ACCOUNT_AUDIT_EMPTY_VERIFIER: "ACCOUNT_AUDIT_EMPTY_VERIFIER",
  ADD_TO_STUDENT_GROUP: "ADD_TO_STUDENT_GROUP",
  REMOVE_FROM_STUDENT_GROUP: "REMOVE_FROM_STUDENT_GROUP",
  ATTEMPT_IMPORT_COMPLETE: "ATTEMPT_IMPORT_COMPLETE",
  ATTEMPT_IMPORT_FAILED: "ATTEMPT_IMPORT_FAILED",
  TEACHER_SUBMIT_MARKS: "TEACHER_SUBMIT_MARKS",
  NODES_RECOMMENDATION_FOR_ALS: "NODES_RECOMMENDATION_FOR_ALS",
  NEW_TOPIC_RESOURCE_COMMENT: "NEW_TOPIC_RESOURCE_COMMENT",
  HEATMAP_NOTIFICATION: "HEATMAP_NOTIFICATION",
  GROUP_REQUEST_ACCESS: "GROUP_REQUEST_ACCESS",
  COMMUNITY_REQUEST_ACCESS: "COMMUNITY_REQUEST_ACCESS",
  AWARD_DIGITALBADGE: "AWARD_DIGITALBADGE",
  ACCOUNT_DEACTIVATION: "ACCOUNT_DEACTIVATION",
  QUESTION_ATTEMPT_FEEDBACK_MENTION: "QUESTION_ATTEMPT_FEEDBACK_MENTION",
  ANNOTATION_MENTION: "ANNOTATION_MENTION",
  ANNOTATION_COMMENT_MENTION: "ANNOTATION_COMMENT_MENTION",
  QUIZ_ATTEMPT_FEEDBACK_MENTION: "QUIZ_ATTEMPT_FEEDBACK_MENTION",
  DISCUSSION_POST_MENTION: "DISCUSSION_POST_MENTION",
  DISCUSSION_POST_COMMENT_MENTION: "DISCUSSION_POST_COMMENT_MENTION",
  POST_MENTION: "POST_MENTION",
  POST_COMMENT_MENTION: "POST_COMMENT_MENTION",
  REMOVE_FROM_COMMUNITY: "REMOVE_FROM_COMMUNITY",
  ADD_TO_COMMUNITY: "ADD_TO_COMMUNITY",
  TEACHER_REVIEW_NOTIFICATION: "TEACHER_REVIEW_NOTIFICATION",
  ASSIGNMENT_SHARING_ENABLED: "ASSIGNMENT_SHARING_ENABLED",
  ASSIGNMENT_REMIND_ENABLE_SHARING: "ASSIGNMENT_REMIND_ENABLE_SHARING",
  BUDDY_REQUESTED: "BUDDY_REQUESTED",
  BUDDY_ACCEPTED: "BUDDY_ACCEPTED",
  COMMENT_CORRECTED: "COMMENT_CORRECTED",
} as const;
